import { useEffect, useMemo, useState } from 'react'
import { Title } from '@app/components/core/typography/Title'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { DashboardPageLayout } from '@app/pages/layout/DashboardPageLayout'
import { useTranslations } from '@i18n/useLanguage'
import { InitiativesPanel } from './sections/shared/InitiativesPanel'
import { FollowingTab } from './sections/following/FollowingTab'
import { InterestListTab } from './sections/following/InterestListTab'
import { useInterestListInitiatives } from './sections/following/useInterestListInitiatives'
import useMediaQuery from '@app/util/useMediaQuery'
import { match } from 'ts-pattern'

const profileFollowingTabs = {
  following: 'following',
  interestList: 'interestList',
} as const

type ProfileFollowingTab =
  (typeof profileFollowingTabs)[keyof typeof profileFollowingTabs]

export const ProfileFollowing = () => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app.dashboard.followed
  const { initiatives: interestListInitiatives } = useInterestListInitiatives()
  const hasInterestListInitiatives = interestListInitiatives.length > 0
  const [tab, setTab] = useState<ProfileFollowingTab>(
    profileFollowingTabs.following,
  )

  useEffect(() => {
    if (
      !hasInterestListInitiatives &&
      tab === profileFollowingTabs.interestList
    ) {
      setTab(profileFollowingTabs.following)
    }
  }, [hasInterestListInitiatives, tab])

  const tabs = useMemo(
    () => [
      {
        value: profileFollowingTabs.following,
        label: translations.tabs.following,
      },
      {
        value: profileFollowingTabs.interestList,
        label: translations.tabs.interestList,
        isVisible: hasInterestListInitiatives,
      },
    ],
    [
      translations.tabs.following,
      translations.tabs.interestList,
      hasInterestListInitiatives,
    ],
  )

  return (
    <DashboardPageLayout>
      <Title.H3>{translations.title}</Title.H3>
      <Row
        gap="1.5rem"
        style={{ alignItems: 'start', flexWrap: 'wrap', flexGrow: 0 }}
      >
        <Column
          alignItems="stretch"
          gap="2rem"
          style={{
            backgroundColor: 'var(--palette-white)',
            padding: '1.5rem 1rem',
            borderRadius: '1.25rem',
            flex: '1 1 30rem',
            minWidth: isMobile ? '100%' : '30rem',
          }}
        >
          <TabsGroup selectedTab={tab} onSelectTab={setTab} tabs={tabs} />
          {match(tab)
            .with(profileFollowingTabs.following, () => <FollowingTab />)
            .with(profileFollowingTabs.interestList, () => <InterestListTab />)
            .exhaustive()}
        </Column>
        <InitiativesPanel />
      </Row>
    </DashboardPageLayout>
  )
}
